/**
 * @class DDSReader
 * @constructor
 * @see https://github.com/KhronosGroup/WebGL/blob/master/sdk/tests/conformance/extensions/webgl-compressed-texture-s3tc.html
 * @see http://msdn.microsoft.com/en-us/library/windows/desktop/bb943982%28v=vs.85%29.aspx
 * @see http://msdn.microsoft.com/en-us/library/windows/desktop/bb943984%28v=vs.85%29.aspx
 */
function DDSReader(){
	this.mipmaps = [];
}

DDSReader.DDS_MAGIC = 0x20534444;
DDSReader.DDS_FLAG_MIPMAPCOUNT_MASK = 0x20000;// named as DDSD_MIPMAPCOUNT
DDSReader.DDS_HEADER_LENGTH = 31; // The header length in 32 bit ints

// Offsets into the header array
DDSReader.DDS_MAGIC_POS = 0;
DDSReader.DDS_SIZE_POS = 1;
DDSReader.DDS_FLAGS_POS = 2;
DDSReader.DDS_HEIGHT_POS = 3;
DDSReader.DDS_WIDTH_POS = 4;

DDSReader.DDS_MIPMAP_COUNT_POS = 7;
// Pixel format flags position
DDSReader.DDS_PFFLAGS_POS = 20;
DDSReader.DDS_FOURCC_POS = 21;
DDSReader.DDS_PFFLAG_FOURCC_MASK = 0x4;
/*
 value = "DXT1";

 return value.charCodeAt(0) +
 (value.charCodeAt(1) << 8) +
 (value.charCodeAt(2) << 16) +
 (value.charCodeAt(3) << 24);
 */
DDSReader.FOURCC_DXT1 = 0x31545844;//"DXT1"
DDSReader.FOURCC_DXT5 = 0x35545844;//"DXT5"

// Equivalent to canvas.getContext("webgl").getExtension("WEBGL_compressed_texture_s3tc").COMPRESSED_RGB_S3TC_DXT1_EXT
// But require an access to the canvas, the context or the extension
// That require a translation
DDSReader.RGB_S3TC_DXT1 = "rgb_s3tc_dxt1";
DDSReader.RGBA_S3TC_DXT1 = "rgba_s3tc_dxt1";
DDSReader.RGBA_S3TC_DXT3 = "rgba_s3tc_dxt3";
DDSReader.RGBA_S3TC_DXT5 = "rgba_s3tc_dxt5";

/** @type Array.<DDSMipmapLevel> */
DDSReader.prototype.mipmaps = null;

/**
 * Validate DDS format
 * S3TC (DXT1 and DXT5 but not DXT3)
 */
DDSReader.prototype._validate = function _validate(buffer){
	var header = new Int32Array(buffer, 0, DDSReader.DDS_HEADER_LENGTH);
	var fourCC;

	if(header[DDSReader.DDS_MAGIC_POS] != DDSReader.DDS_MAGIC)
		throw new Error("Invalid magic number in DDS header");


	if(!header[DDSReader.DDS_PFFLAGS_POS] & DDSReader.DDS_PFFLAG_FOURCC_MASK) {
		throw new Error("Unsupported format, must contain a FourCC code");
	}

	fourCC = header[DDSReader.DDS_FOURCC_POS];
	if(fourCC != DDSReader.FOURCC_DXT1 && fourCC != DDSReader.FOURCC_DXT5){
		throw new Error("Unsupported FourCC code: " +
			String.fromCharCode(
				fourCC & 0xff,
				(fourCC >> 8) & 0xff,
				(fourCC >> 16) & 0xff,
				(fourCC >> 24) & 0xff
			)
		);
	}

	//TODO validate power of 2 of texture size
};

/**
 * Upload DSS image (a group of mipmap levels: 0 to n)
 * Must be a valid and supported DDS (DXT compression for all mipmap levels)
 * @param {ArrayBuffer} buffer
 */
DDSReader.prototype.read = function read(buffer){
	this._validate(buffer);

	var header = new Int32Array(buffer, 0, DDSReader.DDS_HEADER_LENGTH);
	var fourCC;
	var blockBytes;
	var internalFormat;
	var width;
	var height;
	var dataLength;
	var dataOffset;
	var mipmapCount;
	var i;

	fourCC = header[DDSReader.DDS_FOURCC_POS];
//	console.log(fourCC);
	switch(fourCC) {
		case DDSReader.FOURCC_DXT1:
			blockBytes = 8;
			internalFormat = DDSReader.RGBA_S3TC_DXT1;
			break;

		case DDSReader.FOURCC_DXT5:
			blockBytes = 16;
			internalFormat = DDSReader.RGBA_S3TC_DXT5;
			break;

		default:
			return 0;
	}

	mipmapCount = 1;
	// Has more than one mipmap
	if(header[DDSReader.DDS_FLAGS_POS] & DDSReader.DDS_FLAG_MIPMAPCOUNT_MASK) {
		mipmapCount = Math.max(1, header[DDSReader.DDS_MIPMAP_COUNT_POS]);
	}

	width = header[DDSReader.DDS_WIDTH_POS];
	height = header[DDSReader.DDS_HEIGHT_POS];
	dataOffset = header[DDSReader.DDS_SIZE_POS] + 4;

	// Upload mipmap levels (always first level 0, others are extra) using DXT format compression (see `internalFormat`)
	for(i = 0; i < mipmapCount; ++i) {
		dataLength = Math.max( 4, width )/4 * Math.max( 4, height )/4 * blockBytes;

		this.mipmaps.push(new DDSMipmapLevel(i, width, height, internalFormat, buffer.slice(dataOffset, dataOffset + dataLength)));

		dataOffset += dataLength;
		width *= 0.5;
		height *= 0.5;
	}

	return mipmapCount;
};

/**
 * Mipmap level
 * @param {Number} level
 * @param {Number} width
 * @param {Number} height
 * @param {Number} format Format like DDSReader.RGBA_S3TC_DXT5
 * @param {ArrayBuffer} data
 * @constructor
 */
function DDSMipmapLevel(level, width, height, format, data){
	this.level = level;
	this.width = width;
	this.height = height;
	this.format = format;
	this.data = data;
}

DDSMipmapLevel.prototype.format = null;
DDSMipmapLevel.prototype.level = null;
DDSMipmapLevel.prototype.width = null;
DDSMipmapLevel.prototype.height = null;
DDSMipmapLevel.prototype.data = null;